<template>
    <div>
        <app-bar :title="club.name"/>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <div class="pt-8 pb-12 px-8">
                <div class="pb-10">
                    <v-btn
                        v-if="permitted('admin_club_management')"
                        @click="edit"
                        class="sm mr-5"
                        color="primary"
                    >{{ t('general_action_edit') }}
                    </v-btn>
                    <v-btn
                        v-if="permitted('admin_club_management')"
                        class="sm mr-5"
                        color="primary"
                        outlined
                        @click.prevent="suspend(club)"
                    >
                        {{ club.enabled ? t('general_action_suspend') : t('general_action_unsuspend') }}
                    </v-btn>
                    <confirm-modal
                        v-if="permitted('admin_club_management')"
                        :title="t('general_title_confirm_action')"
                        @agree="del(club)"
                    >
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm"
                                color="orange"
                                outlined
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('general_action_delete') }}</v-btn>
                        </template>
                    </confirm-modal>
                </div>
                <div class="float-left mr-10" style="width: 394px; max-width: 394px">
                    <img
                        v-if="club.photoUrl"
                        :src="club.photoUrl"
                        width="150"
                    />
                    <div class="form-subtitle">{{ t('club_label_description') }}:</div>
                    <div class="form-subtitle-description" v-html="club.description"></div>
                </div>
                <div class="float-left" style="max-width: calc(100% - 434px)">
                    <card-unit :items="items()">
                        <template #title>{{ t('club_label_id') }}: {{ club.id }}</template>
                    </card-unit>
                </div>
                <div class="clear-both pt-8">
                    <div class="form-subtitle mb-4">{{ t('club_label_sections') }}</div>
                    <v-card
                        v-for="section in club.sections"
                        :key="section.id"
                        outlined
                        class="mb-4 pa-4"
                    >
                        <div class="font-weight-bold mb-2">{{ section.title }}</div>
                        <v-simple-table dense>
                            <template #default>
                                <thead>
                                <tr>
                                    <th>{{ t('club_element_label_type') }}</th>
                                    <th>{{ t('club_element_label_name') }}</th>
                                    <th>{{ t('club_element_label_premium') }}</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-if="section.elements.length === 0">
                                    <td colspan="3" class="text-center">{{ t('general_text_no_data') }}</td>
                                </tr>
                                <tr
                                    v-for="element in section.elements"
                                    :key="element.id"
                                >
                                    <td>{{ t(`club_element_type_${element.type}`) }}</td>
                                    <td>{{ element.name }}</td>
                                    <td>{{ element.isPremium ? t('general_text_yes') : t('general_text_no') }}</td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card>
                </div>
            </div>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { Club } from '@/core/models/club'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import CardUnit from '@/components/CardUnit.vue'

    export default defineComponent({
        components: {
            ConfirmModal,
            CardUnit
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id']

            const state = reactive({
                overlay: true,
            })

            const capture = () => {
                store.dispatch('club/get', { id: id, payload: {} }).then(() => {
                    state.overlay = false
                })
            }

            const clubModel = computed(() => <Club>store.getters['club/get'])

            const suspend = (item: Club) => {
                state.overlay = true
                store.dispatch('club/update', {
                    id: item.id,
                    payload: { enabled: ! item.enabled }
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                    capture()
                })
            }

            const edit = () => router.push({ name: 'clubs.show', params: { id: <any>clubModel.value.id } })

            const del = (item: Club) => {
                state.overlay = true
                store.dispatch('club/delete', { id: item.id }).then(() => {
                    router.push({ name: 'clubs' })
                })
            }

            const items = () => {
                return [
                    { text: i18n.t('club_label_country'), value: clubModel.value.country || '-' },
                    { text: i18n.t('club_label_followers_count'), value: clubModel.value.followersCount },
                    { text: i18n.t('club_label_sort_order'), value: clubModel.value.sortOrder },
                    { text: i18n.t('general_label_creation_date'), value: clubModel.value.createdAt.format('DD/MM/YYYY') },
                    { text: i18n.t('general_action_published'), value: clubModel.value.enabled ? i18n.t('general_text_yes') : i18n.t('general_text_no') },
                ]
            }

            onMounted(() => {
                capture()
            })

            return {
                capture,
                items,
                club: clubModel.value,
                edit,
                del,
                suspend,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
